/**
 * «Comparar todos los muros» (OPUS ci, idea 7): la misma opción armada para varios conceptos de la hoja a la
 * vez. Cada concepto tiene su propia opción (guardadas juntas en un grupo); aquí se comparan JUNTAS: lo que la
 * hoja tiene hoy en esos conceptos contra lo que quedaría, un renglón por concepto («Así quedaría tu hoja»).
 *
 * Una opción NO suma al total. «Quedarme con estas opciones» las usa todas en una sola operación (todo o
 * nada) y lo de hoy queda guardado como grupo para «Volver todo a lo de antes».
 */
import { useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Spinner } from '@fluentui/react-components';
import { ArrowSwap16Regular, Delete16Regular, Print16Regular } from '@fluentui/react-icons';
import {
  compararGrupo,
  juntarPorConcepto,
  ladoActualDeLaHoja,
  sinPrecioDelGrupo,
  totalDeRenglones,
  type PlanoOpcion,
  type PlanoRenglon,
  type RenglonDeOpcion,
} from '@supernova/shared';

export interface OpcionesDelGrupoProps {
  /** Las opciones del grupo (una por concepto), todas del mismo estado. */
  opciones: PlanoOpcion[];
  hoja: PlanoRenglon[];
  formatoPrecio: (n: number) => string;
  puedeEditar: boolean;
  obra: string | null;
  plano: string | null;
  usar: (grupoId: string, volver: boolean) => Promise<void>;
  descartar: (grupoId: string) => Promise<void>;
  onCerrar: () => void;
}

const fmtCant = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 3 });
const unidadBonita = (u: string | null | undefined) => (u ?? '').replace(/m2$/i, 'm²').replace(/m3$/i, 'm³');
const corto = (s: string, n = 90) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
const signo = (n: number) => (n > 0.004 ? '+' : n < -0.004 ? '−' : '');

export function OpcionesDelGrupo(p: OpcionesDelGrupoProps) {
  const [confirmar, setConfirmar] = useState<'usar' | 'descartar' | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const miembros = useMemo(() => [...p.opciones].sort((a, b) => (a.grupoOrden ?? 0) - (b.grupoOrden ?? 0)), [p.opciones]);
  const grupoId = miembros[0]?.grupoId ?? '';
  const volver = miembros.length > 0 && miembros.every((o) => o.estado === 'anterior');
  const nombre = miembros[0]?.grupoNombre ?? 'Grupo de opciones';

  // Lo de hoy en cada concepto del grupo (su parte, si el renglón junta más elementos).
  const lados = useMemo(
    () => miembros.map((o) => ladoActualDeLaHoja(p.hoja, o.principalId, o.elementos, o.partes ?? null)),
    [miembros, p.hoja],
  );
  const faltan = lados.filter((l) => !l).length;
  const comp = useMemo(
    () => (faltan === 0 ? compararGrupo(lados.map((l) => l!.renglones), miembros.map((o) => o.renglones)) : null),
    [lados, miembros, faltan],
  );
  const quedaria = useMemo(() => juntarPorConcepto(miembros.flatMap((o) => o.renglones)), [miembros]);
  const conIa = useMemo(() => sinPrecioDelGrupo(miembros.map((o) => o.renglones)), [miembros]);
  const totalesPorMiembro = miembros.map((o) => totalDeRenglones(o.renglones).total);
  const pesos = p.formatoPrecio;

  const hacer = async (que: 'usar' | 'descartar') => {
    setConfirmar(null);
    setTrabajando(true);
    setAviso(null);
    try {
      if (que === 'usar') await p.usar(grupoId, volver);
      else await p.descartar(grupoId);
    } catch (e) {
      setAviso((e as Error).message || (que === 'usar' ? 'No se pudo usar el grupo. Tu hoja no cambió.' : 'No se pudo borrar el grupo.'));
    } finally {
      setTrabajando(false);
    }
  };

  const imprimir = () => {
    if (!comp) return;
    const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
    const celda = (r: RenglonDeOpcion | null) =>
      r
        ? `<td>${esc(r.clave ? `${r.clave} · ` : '')}${esc(r.descripcion)}<div class="n2">${esc(fmtCant(r.cantidad))} ${esc(unidadBonita(r.unidad))} × ${r.precioUnitario != null ? esc(pesos(r.precioUnitario)) : 'sin precio'} = <b>${r.importe != null && r.precioUnitario != null ? esc(pesos(r.importe)) : 'se genera con IA'}</b></div></td>`
        : '<td class="no">No lleva</td>';
    const filas = comp.filas
      .map((f) => `<tr${f.cambia ? ' class="c"' : ''}>${celda(f.actual)}${celda(f.opcion)}<td class="d">${signo(f.diferencia)}${esc(pesos(Math.abs(f.diferencia)))}</td></tr>`)
      .join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Comparación · ${esc(nombre)}</title><style>@page{size:letter;margin:14mm}body{font:11.5px system-ui,sans-serif;color:#111;margin:0}h1{font-size:15px;margin:0 0 2px}p{margin:0 0 8px;color:#555}.cifras{display:flex;gap:10px;margin:10px 0}.cifras div{flex:1;border:1px solid #ddd;border-radius:6px;padding:6px 8px}.cifras b{display:block;font-size:15px}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #ddd;padding:5px 6px;vertical-align:top;text-align:left}th{background:#f3f3f3}td.d{text-align:right;white-space:nowrap}.n2{font-size:10.5px;color:#555;margin-top:2px}.no{color:#888}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}tr.c td{background:#fff4c2}.pie{margin-top:10px;font-size:10.5px;color:#555}</style></head><body>` +
        `<h1>${esc(nombre)}</h1><p>${esc(p.obra ?? '')}${p.plano ? ` · ${esc(p.plano)}` : ''} · ${esc(new Date().toLocaleDateString('es-MX'))} · ${miembros.length} conceptos</p>` +
        `<div class="cifras"><div>En tu hoja ahora<b>${esc(pesos(comp.totalActual))}</b></div><div>${volver ? 'Si vuelves a lo de antes' : 'Si cambias'}<b>${esc(pesos(comp.totalOpcion))}</b>${conIa ? `${conIa} sin precio todavía` : ''}</div><div>Diferencia<b>${signo(comp.diferencia)}${esc(pesos(Math.abs(comp.diferencia)))}</b>${comp.porcentaje != null && !conIa ? esc(`${Math.abs(Math.round(comp.porcentaje))} %`) : ''}</div></div>` +
        `<table><thead><tr><th>En tu hoja ahora</th><th>${volver ? 'Si vuelves a lo de antes' : 'Si cambias'}</th><th style="text-align:right">Diferencia</th></tr></thead><tbody>${filas}</tbody></table>` +
        `<div class="pie">En amarillo, lo que cambia. Un renglón por concepto: lo igual de varios conceptos se junta. Lo que acompaña a la opción es una propuesta para revisar.${conIa ? ' Lo que no está en tus catálogos se genera con IA al usar las opciones: no entra en el total.' : ''}</div></body></html>`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <>
      <Dialog open onOpenChange={(_, d) => !d.open && !trabajando && confirmar == null && p.onCerrar()}>
        <DialogSurface style={{ width: 1000, maxWidth: 'calc(100vw - 32px)' }}>
          <DialogBody>
            <DialogTitle>
              {corto(nombre, 110)}
              <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)', marginTop: 2 }}>
                {miembros.length} conceptos de tu hoja, comparados juntos
              </div>
            </DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {aviso && <div style={estilos.aviso}>{aviso}</div>}
              {faltan > 0 && (
                <div style={estilos.aviso}>
                  {faltan === 1 ? 'Uno de los conceptos' : `${faltan} de los conceptos`} del grupo ya no está en la hoja o cambió, así que no se puede comparar. Borra el grupo y
                  arma otro.
                </div>
              )}
              <div style={estilos.guia}>
                <b>Tu hoja todavía no cambió.</b> Estás comparando lo que tienes (✓ en tu hoja ahora) contra {volver ? 'lo que tenías antes' : 'estas opciones, todas juntas'}. Si te
                conviene, pulsa <b>«{volver ? 'Volver todo a lo de antes' : 'Quedarme con estas opciones'}»</b>: tu hoja cambia en todos los conceptos a la vez y lo que tienes hoy se guarda
                para poder volver.
              </div>
              {comp && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                    <Cifra marca="✓ En tu hoja ahora" colorMarca="var(--ok)" valor={pesos(comp.totalActual)} sub="Lo que suman hoy estos conceptos" />
                    <Cifra
                      marca={volver ? 'Si vuelves a lo de antes' : 'Si cambias'}
                      colorMarca="var(--accent)"
                      valor={pesos(comp.totalOpcion)}
                      sub={['Todavía no está en tu hoja', conIa ? `${conIa} sin precio todavía` : null].filter(Boolean).join(' · ')}
                    />
                    <Cifra
                      marca="Diferencia si cambias"
                      valor={`${signo(comp.diferencia)}${pesos(Math.abs(comp.diferencia))}`}
                      color={comp.diferencia < 0 ? 'var(--ok)' : comp.diferencia > 0 ? 'var(--bad)' : undefined}
                      sub={
                        conIa || comp.sinPrecioActual
                          ? `Sin contar ${conIa + comp.sinPrecioActual === 1 ? '1 concepto sin precio' : `${conIa + comp.sinPrecioActual} conceptos sin precio`}`
                          : comp.porcentaje == null
                            ? ''
                            : Math.abs(comp.diferencia) < 0.005
                              ? 'Cuestan lo mismo'
                              : `${comp.diferencia < 0 ? 'Ahorras' : 'Cuesta'} ${Math.abs(Math.round(comp.porcentaje))} %${comp.diferencia < 0 ? '' : ' más'}`
                      }
                    />
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 600 }}>Por concepto</div>
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                    {miembros.map((o, i) => {
                      const l = lados[i]!;
                      const hoy = totalDeRenglones(l.renglones).total;
                      const dif = Math.round((totalesPorMiembro[i]! - hoy) * 100) / 100;
                      return (
                        <div key={o.id} style={{ display: 'flex', gap: 10, justifyContent: 'space-between', padding: '5px 8px', borderBottom: '1px solid var(--border-subtle)', fontSize: 12.5 }}>
                          <span style={{ minWidth: 0 }}>
                            {corto(l.principal.conceptoDescripcion, 70)} <span style={{ color: 'var(--text-tertiary)' }}>→ {corto(o.nombre, 60)}</span>
                          </span>
                          <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 600, color: dif < 0 ? 'var(--ok)' : dif > 0 ? 'var(--bad)' : undefined }}>
                            {signo(dif)}
                            {pesos(Math.abs(dif))}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 600 }}>Así quedaría tu hoja ({quedaria.length} renglones, uno por concepto)</div>
                  <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-secondary)' }}>
                    <span>
                      <span style={{ display: 'inline-block', width: 12, height: 12, background: 'var(--warn-weak)', border: '1px solid var(--warn)', borderRadius: 3, verticalAlign: -2, marginRight: 5 }} />
                      Fila en ámbar: <b>cambia</b> (otro concepto, otra cantidad o solo existe de un lado)
                    </span>
                  </div>
                  <div style={{ maxHeight: '38vh', overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr>
                          <th style={estilos.th}>✓ En tu hoja ahora</th>
                          <th style={estilos.th}>{volver ? 'Si vuelves a lo de antes' : 'Si cambias'}</th>
                          <th style={{ ...estilos.th, textAlign: 'right' }}>Diferencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comp.filas.map((f, i) => (
                          <tr key={i} style={{ background: f.cambia ? 'var(--warn-weak)' : undefined }}>
                            <Celda r={f.actual} pesos={pesos} />
                            <Celda r={f.opcion} pesos={pesos} />
                            <td style={{ ...estilos.td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: f.diferencia < 0 ? 'var(--ok)' : f.diferencia > 0 ? 'var(--bad)' : undefined }}>
                              {signo(f.diferencia)}
                              {pesos(Math.abs(f.diferencia))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={estilos.sub}>Lo que acompaña a las opciones (castillos, dalas, aplanados…) es una propuesta para revisar. Lo igual de varios conceptos se junta en un solo renglón.</div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {p.puedeEditar && (
                <Button appearance="subtle" icon={<Delete16Regular />} disabled={trabajando} onClick={() => setConfirmar('descartar')}>
                  Borrar el grupo
                </Button>
              )}
              <Button appearance="secondary" icon={<Print16Regular />} disabled={!comp} onClick={imprimir}>
                Imprimir comparación
              </Button>
              {p.puedeEditar && (
                <Button appearance="primary" icon={trabajando ? <Spinner size="extra-tiny" /> : <ArrowSwap16Regular />} disabled={trabajando || !comp} onClick={() => setConfirmar('usar')}>
                  {volver ? 'Volver todo a lo de antes' : 'Quedarme con estas opciones'}
                </Button>
              )}
              <Button appearance="secondary" disabled={trabajando} onClick={p.onCerrar}>
                Cerrar sin cambiar nada
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={confirmar != null} onOpenChange={(_, d) => !d.open && setConfirmar(null)}>
        <DialogSurface style={{ maxWidth: 480 }}>
          <DialogBody>
            <DialogTitle>{confirmar === 'usar' ? 'Cambiar tu hoja' : 'Borrar el grupo'}</DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13.5 }}>
              {confirmar === 'usar' && (
                <>
                  <div>
                    {volver
                      ? `Tu hoja vuelve a lo que tenía antes en estos ${miembros.length} conceptos.`
                      : `Tu hoja cambia en estos ${miembros.length} conceptos a la vez, todo o nada: si algo no se puede, no cambia nada. Lo que tienes hoy se guarda para volver cuando quieras.`}
                  </div>
                  {!volver && conIa > 0 && (
                    <div style={{ fontWeight: 600 }}>
                      {conIa === 1 ? '1 concepto necesita IA' : `${conIa} conceptos necesitan IA`}: no está en tus catálogos y entra marcado para «Generar con IA», que consume de tu
                      saldo del mes cuando lo pidas. ¿Confirmas?
                    </div>
                  )}
                </>
              )}
              {confirmar === 'descartar' && <div>Se borra el grupo «{corto(nombre, 80)}» completo. Tu hoja no cambia.</div>}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmar(null)}>
                Cancelar
              </Button>
              <Button appearance="primary" onClick={() => void hacer(confirmar === 'usar' ? 'usar' : 'descartar')}>
                {confirmar === 'usar' ? 'Sí, cambiar mi hoja' : 'Borrar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

function Celda({ r, pesos }: { r: RenglonDeOpcion | null; pesos: (n: number) => string }) {
  if (!r) return <td style={{ ...estilos.td, color: 'var(--text-tertiary)' }}>No lleva</td>;
  return (
    <td style={estilos.td}>
      <div style={{ lineHeight: 1.35 }}>
        {r.clave ? <b>{r.clave} · </b> : null}
        {corto(r.descripcion, 140)}
      </div>
      {r.sinCantidad ? (
        <div style={{ ...estilos.sub, color: 'var(--bad)', fontWeight: 600 }}>Sin cantidad: el plano no la da. Entra en 0 y marcada para que la escribas en la hoja.</div>
      ) : (
        <div style={{ ...estilos.sub, fontVariantNumeric: 'tabular-nums' }}>
          {fmtCant(r.cantidad)} {unidadBonita(r.unidad)} × {r.precioUnitario != null ? pesos(r.precioUnitario) : 'sin precio'} ={' '}
          <b style={{ color: 'var(--text-primary)' }}>{r.importe != null && r.precioUnitario != null ? pesos(r.importe) : 'se genera con IA'}</b>
        </div>
      )}
    </td>
  );
}

function Cifra({ marca, colorMarca, valor, sub, color }: { marca: string; colorMarca?: string; valor: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'var(--bg-raised)', borderRadius: 6, padding: '8px 10px', minWidth: 0, borderTop: colorMarca ? `3px solid ${colorMarca}` : undefined }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, color: colorMarca ?? 'var(--text-tertiary)' }}>{marca}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sub}</div>}
    </div>
  );
}

const estilos = {
  th: {
    position: 'sticky' as const,
    top: 0,
    background: 'var(--bg-raised)',
    textAlign: 'left' as const,
    padding: '6px 8px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border-subtle)',
    zIndex: 1,
  },
  td: { padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' as const },
  sub: { fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 },
  guia: { fontSize: 13, lineHeight: 1.45, background: 'var(--bg-raised)', borderLeft: '3px solid var(--accent)', borderRadius: 6, padding: '8px 12px' },
  aviso: { fontSize: 13, color: 'var(--warn)', background: 'var(--warn-weak)', borderRadius: 6, padding: '6px 10px' },
};
